import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';

export type RewardStatusResponse = {
  cabinetId: number | null;
  generationCreditsAvailable: number;
  pendingStripeDiscount: {
    redemptionId: number;
    codeSlug: string;
    stripeCouponId: string | null;
    effectType: string;
    status: string;
  } | null;
  redemptions: Array<{
    id: number;
    codeSlug: string;
    effectType: string;
    status: string;
    stripeCouponId: string | null;
    consumedAt: string | null;
  }>;
};

export type FetchRewardStatusResult =
  | { ok: true; data: RewardStatusResponse }
  | { ok: false; status: number; message: string };

type RewardRequestParams = {
  baseUrl: string;
  nonce: string;
};

export const fetchRewardStatus = async ({
  baseUrl,
  nonce,
}: RewardRequestParams): Promise<FetchRewardStatusResult> => {
  const response = await fetchWithNonce(`${baseUrl}rewards/status`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': nonce },
  });

  if (response.status === 401 || response.status === 403) {
    return { ok: false, status: response.status, message: '' };
  }

  if (!response.ok) {
    return { ok: false, status: response.status, message: await parseApiError(response) };
  }

  const data = (await response.json()) as RewardStatusResponse;
  return { ok: true, data };
};

export type RedeemRewardResult =
  | { ok: true; data: { redemption_id: number; effect_type: string; status: string; code_slug: string; stripe_coupon_id: string | null } }
  | { ok: false; status: number; message: string };

export const postRedeemRewardCode = async ({
  baseUrl,
  nonce,
  code,
}: RewardRequestParams & { code: string }): Promise<RedeemRewardResult> => {
  const response = await fetchWithNonce(`${baseUrl}rewards/redeem`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-WP-Nonce': nonce,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (response.status === 401 || response.status === 403) {
    return { ok: false, status: response.status, message: '' };
  }

  if (!response.ok) {
    return { ok: false, status: response.status, message: await parseApiError(response) };
  }

  const data = (await response.json()) as {
    redemption_id: number;
    effect_type: string;
    status: string;
    code_slug: string;
    stripe_coupon_id: string | null;
  };

  return {
    ok: true,
    data: {
      redemption_id: data.redemption_id,
      effect_type: data.effect_type,
      status: data.status,
      code_slug: data.code_slug,
      stripe_coupon_id: data.stripe_coupon_id,
    },
  };
};
